import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, isToday, isTomorrow, parseISO, differenceInHours, isValid } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, User, AlertTriangle, Flame, XCircle, WifiOff, ChevronRight, MapPin } from "lucide-react";
import { useBookingsSupabaseStore } from "@/store/api/bookingsSupabaseStore";
import { BookingData } from "@/types/types";
import { CancelBookingDialog } from "./CancelBookingDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useUserStore } from "@/store/userStore";
import { StatusBadge } from "@/components/attendance/table/StatusBadge";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";
import { AthleteHeroBackground } from "@/components/athlete/AthleteHeroBackground";

interface ReservationsViewProps {
  limit?: number;
}

export function ReservationsView({ limit }: ReservationsViewProps = {}) {
  const { t, i18n } = useTranslation(["athlete", "common"]);
  const locale = i18n.language === 'fr' ? fr : enUS;
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { bookings, fetchBookings, isLoading, error } = useBookingsSupabaseStore();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<BookingData | null>(null);
  const { user } = useUserStore();
  const { athleteTheme, athleteBackgrounds, brandName } = useThemeCustomization();
  const isVb20 = athleteTheme.variant === "dark-premium";

  useEffect(() => {
    // Un seul chargement : l'appel direct à `getUserBookings` qui précédait
    // était doublon (son résultat était jeté) et déclenchait deux requêtes.
    fetchBookings();
  }, [fetchBookings, user]);

  // `user` est typé `User | null` et `clearAuth()` le passe à `null` pendant
  // que le composant est encore monté : `user.id` levait en plein rendu.
  const now = Date.now();
  const allUpcomingBookings = bookings
    .filter((booking) => {
      const startsAt = new Date(`${booking.date}T${booking.time || "00:00"}`).getTime();
      return booking.status === 'upcoming' && booking.userId === user?.id && Number.isFinite(startsAt) && startsAt >= now;
    })
    .sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateA.getTime() - dateB.getTime();
    });

  const upcomingBookings = limit ? allUpcomingBookings.slice(0, limit) : allUpcomingBookings;
  const hasMoreBookings = limit && allUpcomingBookings.length > limit;
  const nextBookingId = allUpcomingBookings[0]?.id;

  const isWithin24Hours = (booking: BookingData) => {
    try {
      const bookingDate = parseISO(booking.date);
      if (!isValid(bookingDate)) return false;
      const bookingTime = (booking.time || '').split(':').map(Number);
      if (Number.isNaN(bookingTime[0]) || Number.isNaN(bookingTime[1])) return false;
      bookingDate.setHours(bookingTime[0], bookingTime[1], 0);
      return differenceInHours(bookingDate, new Date()) < 24;
    } catch (error) {
      return false;
    }
  };

  function humanDate(date: string, time: string) {
    // L'edge function renvoie `time: ''` en repli : `new Date("2026-01-05T")`
    // est invalide et `format` LÈVE (date-fns v3) — écran blanc en plein rendu.
    const d = time ? new Date(`${date}T${time}`) : new Date(date);
    if (!isValid(d)) return date || "";
    if (isToday(d)) return t("reservation.today", { ns: "athlete", defaultValue: "Aujourd'hui" }); // + ' à ' + format(d, 'HH:mm');
    if (isTomorrow(d)) return t("reservation.tomorrow", { ns: "athlete", defaultValue: "Demain" });// + ' à ' + format(d, 'HH:mm');
    return format(d, "EEEE d MMMM", { locale }); //'à' HH:mm
  }

  // Une panne réseau ne doit PAS s'afficher « Aucune réservation à venir » :
  // l'adhérent croyait n'avoir rien réservé et re-réservait par-dessus.
  const loadErrorPanel = (
    <div className="text-center py-12 flex flex-col items-center">
      <WifiOff className="h-12 w-12 text-destructive mb-4" />
      <div className="text-lg font-semibold">
        {t('reservation.loadError', { ns: 'athlete', defaultValue: "Impossible de charger tes réservations" })}
      </div>
      <p className="text-muted-foreground mt-2 max-w-sm">
        {t('reservation.loadErrorHint', { ns: 'athlete', defaultValue: "La liste ci-dessous peut être incomplète. Vérifie ta connexion avant de réserver à nouveau." })}
      </p>
      <Button variant="outline" className="mt-4" onClick={() => fetchBookings()}>
        {t('common.retry', { ns: 'common', defaultValue: 'Réessayer' })}
      </Button>
    </div>
  );

  const noBookingsCTA = (
    <div className="text-center py-12 flex flex-col items-center">
      <Flame className="h-12 w-12 text-orange-400 mb-4 animate-bounce" />
      <div className="text-lg font-semibold">{t('reservation.noUpcoming', { ns: 'athlete' })}</div>
      <p className="text-muted-foreground mt-2">{t('reservation.bookCta', { ns: 'athlete', defaultValue: "Réserve vite ta prochaine séance pour garder le rythme ! 💪" })}</p>
    </div>
  );

  const handleCancelReservation = (booking: BookingData) => {
    setSelectedBooking(booking);
    setIsCancelDialogOpen(true);
  };
  const confirmCancellation = async () => {
    await fetchBookings();
  };

  if (isLoading) {
    return (
      <div className="w-full px-0">
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-36 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full px-0 md:px-0">
      <Card className="athlete-surface athlete-reservations-panel rounded-lg shadow-none">
        <CardHeader className="px-4 md:px-8 pt-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-[22px] font-bold flex items-center gap-2">
                 {!isVb20 && <User className="w-6 h-6 text-primary" />}
                {t('reservation.upcomingTitle', { ns: 'athlete' })}
              </CardTitle>
              <CardDescription className={cn("mt-1", isVb20 && "sr-only")}>
                {t('reservation.upcomingDescription', { ns: 'athlete' })}
              </CardDescription>
            </div>
            {/* Les badges « jokers / séances restantes » lisaient un état
                `subscriptionInfo` jamais renseigné : ils n'ont jamais pu
                s'afficher. Retirés plutôt que laissés en code mort — la donnée
                vit dans l'onglet Abonnement. */}
          </div>
          <div className={cn("mt-6 mb-0 px-3 py-3 bg-primary/10 border border-primary/20 rounded-lg items-center gap-3", isVb20 ? "hidden" : "flex")}>
            <Flame className="text-primary" />
            <span className="text-sm font-medium">
              {t('reservation.motivation', { ns: 'athlete', defaultValue: "Garde le rythme ! Chaque séance compte pour ta progression." })}
            </span>
          </div>
        </CardHeader>

        <CardContent className="px-2 md:px-8">
          {/* Liste potentiellement périmée : on le signale au lieu de laisser
              croire qu'elle est à jour. */}
          {error && upcomingBookings.length > 0 && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <WifiOff className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {t('reservation.staleWarning', {
                  ns: 'athlete',
                  defaultValue: "Liste peut-être incomplète : le rafraîchissement a échoué.",
                })}
              </span>
            </div>
          )}
          {upcomingBookings.length === 0
            ? (error ? loadErrorPanel : noBookingsCTA)
            : (
              <div className="flex flex-col gap-6">
                {upcomingBookings.map((booking, idx) => {
                  const within24h = isWithin24Hours(booking as BookingData);
                  const isNext = booking.id === nextBookingId;
                  return (
                    <div
                      key={booking.id}
                      className={cn(
                         "athlete-upcoming-session-card relative overflow-hidden rounded-lg shadow flex items-stretch justify-between border border-border p-3 bg-card transition hover:border-primary/60 w-full",
                        isNext ? "ring-1 ring-primary" : ""
                      )}
                      style={{ animation: `fadeIn .4s ${(idx + 1) * 0.1}s both` }}
                    >
                       {isVb20 && (
                         <div className="relative mr-3 block w-[28%] min-w-[5.25rem] shrink-0 overflow-hidden rounded-md">
                           <AthleteHeroBackground src={athleteBackgrounds.reservations} className="absolute inset-0 h-full w-full object-cover" />
                         </div>
                       )}
                       <div className="flex-1 min-w-0 py-1">
                        <div className="flex items-center gap-2">
                           <span className="font-bold text-lg">{booking.className}</span>
                           <span className="athlete-upcoming-status ml-auto rounded-full px-2 py-1 text-[11px] font-semibold">
                             {isNext
                               ? t("reservation.confirmed", { ns: "athlete", defaultValue: "Confirmé" })
                               : t("reservation.booked", { ns: "athlete", defaultValue: "Réservé" })}
                           </span>
                        </div>
                         <div className="mt-2 space-y-1 text-sm">
                          <span className="flex items-center text-muted-foreground">
                            <Calendar className="h-4 w-4 mr-1" />
                             {humanDate(booking.date, booking.time)} · {booking.time}
                          </span>
                          <span className="flex items-center text-muted-foreground">
                            <Clock className="h-4 w-4 mr-1" />
                             {booking.duration} min
                          </span>
                           <span className="flex items-center text-muted-foreground"><User className="h-4 w-4 mr-1" />{booking.coach}</span>
                           <span className="flex items-center text-muted-foreground"><MapPin className="h-4 w-4 mr-1" />{brandName}</span>
                        </div>
                        {within24h && (
                          <div className="mt-3 flex items-start space-x-2 text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 p-2 rounded-md text-xs">
                            <AlertTriangle className="h-4 w-4 mt-0.5" />
                            <div>
                              {t('reservation.within24hWarning', { ns: 'athlete' })}
                            </div>
                          </div>
                        )}
                      </div>
                       <div className="ml-2 flex shrink-0 flex-col items-end justify-between gap-2 py-1">
                         {!isVb20 && <StatusBadge status={booking.status as 'upcoming' | 'completed' | 'cancelled'} isMobile={isMobile} />}
                         {isVb20 && <ChevronRight className="mt-auto h-5 w-5 text-primary" />}
                        <Button
                          variant="ghost"
                          size="icon"
                           className="text-destructive border border-transparent hover:bg-destructive/10"
                          onClick={() => handleCancelReservation(booking as BookingData)}
                          aria-label={t('reservation.cancel', { ns: 'athlete' })}
                        >
                          <XCircle className="w-6 h-6" />
                          <span className="sr-only">{t('reservation.cancel', { ns: 'athlete' })}</span>
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {hasMoreBookings && (
                  <div className="mt-6 text-center">
                    <Button 
                      variant="outline" 
                      className="w-full md:w-auto"
                      onClick={() => navigate('/athlete/reservations')}
                    >
                      {t('reservation.viewAll', { ns: 'athlete', defaultValue: 'Voir toutes mes réservations' })} ({allUpcomingBookings.length})
                    </Button>
                  </div>
                )}
              </div>
            )
          }
        </CardContent>
        {selectedBooking && (
          <CancelBookingDialog
            open={isCancelDialogOpen}
            onOpenChange={setIsCancelDialogOpen}
            booking={selectedBooking}
            onConfirm={confirmCancellation}
          />
        )}
      </Card>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(25px);}
          to { opacity: 1; transform: translateY(0);}
        }
      `}</style>
    </div>
  );
}
